<template>
  <MuiForm
    v-model="formData"
    class="SignUpCompanyStep1 Step__form"
    @submit.native="submit"
  >
    <template v-if="linkAccount.isLinkAccount">
      <h1 class="Step__title h1 color-secondary" />
      <div class="Step__subtitle" />
    </template>
    <template v-else>
      <h1 class="Step__title h1 color-secondary" />
      <div class="Step__subtitle" />
    </template>
    <!-- Admin Login -->
    <span
      v-if="!linkAccount.isLinkAccount"
      class="Step__AdminLoginSection"
    >
      <h3 class="h3 color-secondary pb-3">
        login
      </h3>
      <MuiInputText
        :label="$t('Views.company_signup.fields.company_email')"
        :placeholder="$t('Views.company_signup.fields.company_email')"
        :value="data.companyEmail"
        :on-change="(e, v) => companyEmail(v)"
        :custom-error-messages="{
          forceError: forceErrorOnEmailExistMessage,
          empty: this.$t('Views.company_signup.errors.mandatory'),
          email: this.$t('Views.company_signup.errors.invalid_email'),
        }"
        :force-error="forceErrorOnEmailExist"
        required
        full-width
        name="companyEmail"
        type="email"
      />

      <MuiInputText
        :label="$t('Views.company_signup.fields.password')"
        :placeholder="$t('Views.company_signup.fields.password')"
        :value="data.password"
        :on-change="(e, v) => password(v)"
        :custom-error-messages="{
          empty: this.$t('Views.company_signup.errors.mandatory')
        }"
        :pattern="[
          { rule: /\w*[a-z]\w*/, message: $t('Views.company_signup.errors.password_lowercase') },
          { rule: /\w*[A-Z]\w*/, message: $t('Views.company_signup.errors.password_uppercase') },
          { rule: /\w*[0-9]\w*/, message: $t('Views.company_signup.errors.password_number') },
          { rule: /^.{8,}$/, message: $t('Views.company_signup.errors.password_length') },
          { rule: /^[a-zA-Z0-9]*$/, message: $t('Views.company_signup.errors.no_characters') },
        ]"
        required
        full-width
        name="password"
        type="password"
        validation-type="change"
      />

      <MuiSelect
        v-if="titles.length"
        :label="$t('Views.company_signup.fields.title')"
        :placeholder="$t('Views.company_signup.fields.title')"
        :value="data.title"
        :return-object="true"
        :on-change="(e) => title(e)"
        :custom-error-messages="{
          empty: this.$t('Views.company_signup.errors.mandatory')
        }"
        required
        full-width
        object-label="name"
        object-value="internal_name"
        name="title"
      >
        <MuiSelectItem
          v-for="title in titles"
          :key="title.internal_name"
          :value="title"
          :title="title.name"
          type="main"
        >
          {{ title.name }}
        </MuiSelectItem>
      </MuiSelect>

      <MuiInputText
        :label="$t('Views.company_signup.fields.first_name')"
        :placeholder="$t('Views.company_signup.fields.first_name')"
        :value="data.firstName"
        :on-change="(e, v) => firstName(v)"
        :custom-error-messages="{
          empty: this.$t('Views.company_signup.errors.mandatory')
        }"
        required
        full-width
        type="text"
        name="firstName"
      />

      <MuiInputText
        :label="$t('Views.company_signup.fields.last_name')"
        :placeholder="$t('Views.company_signup.fields.last_name')"
        :value="data.lastName"
        :on-change="(e, v) => lastName(v)"
        :custom-error-messages="{
          empty: this.$t('Views.company_signup.errors.mandatory')
        }"
        required
        full-width
        type="text"
        name="lastName"
      />

      <MuiDate
        v-model="initialDate"
        :label="$t('birthdate_input.label')"
        :min-date="ninetyYears"
        :max-date="eighteenYears"
        :initial-value="null"
        :custom-error-messages="{
          minDate: $t('Views.user_signup.step_3.fields.min_driver_age'),
          maxDate: $t('Views.user_signup.step_3.fields.min_driver_age'),
          empty: $t('Views.company_signup.errors.mandatory'),
          mandatory: $t('Views.company_signup.errors.mandatory'),
        }"
        required
        name="birthday"
        @date="(e) => dateBirth(e)"
      />

      <MuiInputPhone
        :mask="true"
        :country-label="$t('Views.company_signup.fields.country_phone')"
        :label="$t('Views.company_signup.fields.phone')"
        :phone-label-class="$t('Views.company_signup.fields.phone')"
        :value="data.phoneNumber.length > 4 ? data.phoneNumber : ''"
        :custom-error-messages="{
          empty: this.$t('Views.company_signup.errors.mandatory'),
          invalidNumber: $t('Views.company_signup.errors.invalid_phonenumber')
        }"
        required
        name="phoneNumber"
        default-country="es"
        @change="(val) => {
          phoneNumber(val);
        }"
      />
    </span>

    <!-- Company Information -->
    <span>
      <h3 class="Step__form_company-title h3 color-secondary pb-3">
        {{ $t('Views.company_signup.company_information_title') }}
      </h3>
      <MuiInputText
        :label="$t('Views.company_signup.fields.company_name')"
        :placeholder="$t('Views.company_signup.fields.company_name')"
        :value="data.companyName"
        :on-change="(e, value) => companyName(value)"
        :custom-error-messages="{
          empty: this.$t('Views.company_signup.errors.mandatory')
        }"
        required
        full-width
        name="companyName"
      />

      <MuiSelect
        v-if="countries.length"
        :label="$t('Views.company_signup.fields.country')"
        :placeholder="$t('Views.company_signup.fields.country')"
        :value="data.country"
        :return-object="true"
        :on-change="(e) => onChangeCountry(e)"
        :custom-error-messages="{
          empty: this.$t('Views.company_signup.errors.mandatory')
        }"
        required
        full-width
        size="l"
        class="pb-3"
        object-label="name"
        object-value="code"
        name="country"
      >
        <MuiSelectItem
          v-for="country in countries"
          :key="country.code"
          :value="country"
        >
          {{ country.name }}
        </MuiSelectItem>
      </MuiSelect>

      <MuiInputTax
        :prefix-placeholder="$t('Views.company_signup.fields.country_tax')"
        :label="$t('Views.company_signup.fields.tax')"
        :placeholder="$t('Views.company_signup.fields.tax')"
        :value="data.taxNumber"
        :on-change="(e, value) => taxNumber(value)"
        :force-error="forceTaxNumberError"
        :custom-error-messages="{
          forceError: forceTaxNumberErrorMessage,
          empty: this.$t('Views.company_signup.errors.mandatory')
        }"
        full-width
        required
        default-country="es"
        name="taxNumber"
      />

    </span>

    <!-- Work contact information -->
    <span v-if="linkAccount.isLinkAccount">
      <h3 class="Step__form__work-title h3">
        {{ $t('Views.company_signup.fields.contact_info') }}
      </h3>
      <MuiInputText
        :full-width="true"
        :placeholder="$t('Views.company_signup.fields.work_email')"
        :value="data.workEmail"
        :on-change="(e, value) => workEmail(value)"
        :custom-error-messages="{
          empty: this.$t('Views.company_signup.errors.mandatory'),
          email: this.$t('Views.company_signup.errors.invalid_email')
        }"
        required
        name="workEmail"
        type="email"
      />

      <MuiInputPhone
        :country-label="$t('Views.company_signup.fields.country_phone')"
        :placeholder="$t('Views.company_signup.fields.phone')"
        :mask="true"
        :value="data.workPhoneNumber"
        :custom-error-messages="{
          empty: this.$t('Views.company_signup.errors.mandatory'),
          invalidNumber: $t('Views.company_signup.errors.invalid_phonenumber')
        }"
        :force-error="forceErrorOnEmailExist"
        required
        full-width
        name="workPhone"
        default-country="es"
        @change="(val) => {
          workPhoneNumber(val);
        }"
      />
    </span>

    <MuiButtonFlat
      :disabled="btnLoading"
      :submit="true"
      class="Step__form__next-button"
    >
      {{ $t('Views.company_signup.actions.next') }}
    </MuiButtonFlat>
  </MuiForm>
</template>

<script>
import moment from 'moment';
import { mapMutations, mapState, mapActions } from 'vuex';
import {
  MuiForm,
  MuiInputText,
  MuiButtonFlat,
  MuiSelect,
  MuiSelectItem,
  MuiInputPhone,
  MuiDate,
  MuiInputTax,
} from '@ubeeqo/motion-ui';

import StepsMixin from '@/mixins/StepsMixin';

import { COMPANY_MODULE, GENERAL_MODULE } from '@/constants/modules';

export default {
  name: 'SignUpCompanyStep1View',
  components: {
    MuiForm,
    MuiInputText,
    MuiButtonFlat,
    MuiSelect,
    MuiSelectItem,
    MuiInputTax,
    MuiInputPhone,
    MuiDate,
  },
  mixins: [
    StepsMixin,
  ],
  props: {
    auth: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      formData: {},
      btnLoading: false,
      forceTaxNumberError: false,
      forceTaxNumberErrorMessage: '',
      forceErrorOnEmailExist: false,
      forceErrorOnEmailExistMessage: '',
      submitFromClick: false,
      initialDate: {
        day: '',
        month: '',
        year: '',
      },
    };
  },
  computed: {
    // Company Data
    ...mapState(COMPANY_MODULE, {
      data: state => state.data,
      linkAccount: state => state.linkAccount,
    }),
    // GENERAL Data
    ...mapState(GENERAL_MODULE, {
      countries: state => state.countries,
      titles: state => state.titles,
      locale: state => state.locale,
    }),
    getLinkAccountUrl() {
      return this.$router.resolve({ name: '' }).href;
    },
    ninetyYears() {
      const now = moment.utc()
        .hour(0)
        .minutes(0)
        .seconds(0)
        .milliseconds(0);

      return now.subtract(90, 'years').toDate();
    },
    eighteenYears() {
      const now = moment.utc()
        .hour(0)
        .minutes(0)
        .seconds(0)
        .milliseconds(0);

      return now.subtract(18, 'years').toDate();
    },
  },
  async created() {
    let countrySource = this.$route.query.source;
    if (countrySource) {
      countrySource = /uk/i.test(countrySource) ? 'gb' : countrySource;
      // eslint-disable-next-line
      const country = this.countries.find((c) => {
        return c.code.toLowerCase() === countrySource.toLowerCase();
      });
      if (country) {
        this.onChangeCountry(country);
      }
    }
  },
  beforeMount() {
    this.initialDate = (this.data.datebirth && new Date(this.data.datebirth)) || this.initialDate;
  },
  methods: {
    ...mapMutations(COMPANY_MODULE, [
      'companyName',
      'country',
      'taxNumber',
      'workEmail',
      'workPhoneNumber',
      'companyEmail',
      'password',
      'title',
      'firstName',
      'lastName',
      'dateBirth',
      'phoneNumber',
      'doneStep',
      'unDoneStep',
      'updateStep',
      'isLinkAccount',
      'birthdate',
    ]),
    ...mapActions(COMPANY_MODULE, [
      'setPreRegister',
      'setCountry',
    ]),
    goLinkAccount() {
      const route = this.$router.resolve({ name: 'LinkAdminAccount' });
      this.auth.setRedirectUri(`${window.location.origin}${route.href}`);
      this.auth.login();
    },
    async submit() {
      this.forceTaxNumberError = false;
      this.forceErrorOnEmailExist = false;

      if (this.formData.fields.taxNumber.valid) {
        await this.setPreRegister().catch((e) => {
          this.forceTaxNumberError = true;
          this.forceTaxNumberErrorMessage = e.response.data.message;
          this.formData.valid = false;
        });

        if (this.formData.valid === false) {
          return;
        }
      }

      /* if (!this.linkAccount.isLinkAccount && this.formData.fields.companyEmail.valid) {
        const email = this.formData.fields.companyEmail.value;
        await externalApi.checkExistingEmail(email).catch((e) => {
          this.forceErrorOnEmailExistMessage = e.response.data.message;
          this.forceErrorOnEmailExist = true;
          this.formData.valid = false;
        });
      } */

      if (this.formData.valid) {
        this.$steps_handlerFormSubmit();
      }
    },
    onChangeCountry(country) {
      this.setCountry({ country, locale: this.locale });
    },
  },
};
</script>
